import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure, Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { downloadCsv } from '@/lib/csv'
import { formatDateIn } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type Suppression = Schema<'SuppressionOut'>
type Kind = Schema<'SuppressionKind'>
type Reason = Schema<'SuppressionReason'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * `DEFAULT_PAGE` and `MAX_PAGE` in `app/v1/outreach.py`, mirrored.
 *
 * Duplicated rather than derived because OpenAPI describes the parameter's
 * bounds and not its default, and a screen that asked for more than `MAX_PAGE`
 * would get a 422 rather than a truncated list.
 */
const FIRST_PAGE = 50
const MAX_PAGE = 200

/**
 * The two orders the route offers — `SUPPRESSION_ORDERS` in `app/v1/outreach.py`.
 *
 * **A sort here chooses the window, not the arrangement of one** (OUTR-92). The
 * read behind this screen is `ORDER BY … LIMIT n` with no offset, so *contact*
 * asks for the fifty alphabetically first entries and *recent* for the fifty
 * newest — genuinely different rows. That is why the press goes to the server
 * instead of being a `.sort()` over what is already in the browser: a
 * client-side sort would rearrange fifty rows while the header claimed to have
 * ordered two hundred and eighteen, and on a compliance list the row somebody is
 * looking for is exactly the one that is not in the fifty.
 *
 * Both walk one way, so only `descending` is ever announced on `Added` and only
 * `ascending` on `Contact`; claiming a direction the API cannot produce would be
 * the control lying about itself (`Leads.tsx`'s `SortHeader`).
 */
const ORDERS = { recent: 'recent', contact: 'contact' } as const
type Order = (typeof ORDERS)[keyof typeof ORDERS]

/**
 * The suppression list — `design/screens/outreach.html#supp`, audit row §A4,
 * built over the three `suppression:manage` routes in `api/app/v1/outreach.py`.
 *
 * S-B12 shipped `GET`, `POST` and `DELETE` on 2026-08-01 and said so in its own
 * module docstring — *"There is no suppression screen in this session"* — and
 * the sidebar has rendered *Suppression* disabled ever since. This is that
 * screen.
 *
 * WHY THERE IS NO PREVIOUS/NEXT, WHICH THE PROTOTYPE DRAWS
 * ---------------------------------------------------------
 * Because there is no second page to go to. `OutreachTransaction.suppressions`
 * takes a `limit` and no offset — it is `ORDER BY created_at DESC LIMIT n`, and
 * the route exposes exactly that — so *Next* would have nothing to send. What
 * exists instead is a larger `limit`, capped by the server at `MAX_PAGE`, and
 * that is what *Show more* asks for: the ordering is stable, so a bigger limit
 * is a superset of the smaller one rather than a different page.
 *
 * At the cap the screen says so in as many words rather than letting a tenant
 * believe they are looking at the whole list. This is the same call §A3 made
 * about the prototype's *30-day uptime* and §V2 about its ₹4.20: the honest
 * answer is the window that exists, labelled as that window.
 *
 * WHY THE COUNT SAYS "IN TOTAL" WHEN A FILTER IS ON
 * ---------------------------------------------------
 * `SuppressionListResponse.total` comes from `OutreachTransaction.count`, which
 * counts the tenant's whole table and takes no filter. It is therefore the
 * organisation's total and never the number of matches, and the toolbar labels
 * the two separately. Presenting `total` beside a search box as though it
 * described the search is the one reading of this response that is wrong, and
 * it is the reading the prototype's bare *218 suppressed* invites.
 *
 * WHY THE COLUMN IS "KIND" AND NOT THE PROTOTYPE'S "CHANNEL"
 * ------------------------------------------------------------
 * They are not the same thing, and the difference matters on exactly the row a
 * support call is about. `CHANNEL_KINDS` in `app/outreach/suppression.py` maps
 * **four** channels — SMS, WhatsApp, RCS and IVR — onto the single `mobile`
 * kind, so a row rendered as *SMS* would be under-reporting what it stops. And
 * the prototype's *All* is really the `domain` kind, which suppresses every
 * address at a company rather than every channel for one person. The column
 * says which of the four kinds the row is and the caption says what each one
 * stops, which is the pair a tenant can act on.
 *
 * WHY REMOVAL IS OFFERED AT ALL, GIVEN THE PROTOTYPE'S OWN COPY
 * ---------------------------------------------------------------
 * The prototype says entries are *"never quietly removed"*, and the operative
 * word is **quietly**: `DELETE /outreach/suppression/{id}` exists, and it writes
 * `SUPPRESSION_REMOVED` to the audit trail carrying the kind, the value and the
 * reason of the row that went — the route's docstring gives the reason, that
 * *"somebody was put back on a campaign after asking not to be"* cannot be
 * answered from a row that no longer exists. So the control is real and the
 * confirmation says what it will do, rather than the screen pretending a
 * capability the API has is one it does not.
 *
 * WHY A MEMBER SEES A REFUSAL RATHER THAN AN EMPTY TABLE
 * -------------------------------------------------------
 * All three routes are `suppression:manage`, which `app/tenancy/roles.py` gives
 * to owner and admin only — the **read** included, unlike the inbox next door.
 * `navigation.ts` has no permission field and renders every section to
 * everybody, so a member can reach this path; without the check below they
 * would get a 403 rendered as a failed request, which reads as a broken screen
 * rather than as a boundary working.
 */
export function Suppression(): JSX.Element {
  const { membership } = useActiveTenant()
  const manageable = may(membership, 'suppression:manage')

  if (!manageable) return <NotYours />
  return <SuppressionList />
}

/** What a member sees. Not an error: the boundary is behaving. */
function NotYours(): JSX.Element {
  return (
    <div className="ar-stack ar-stack--loose">
      <h1 className="ar-h1">Suppression list</h1>
      <div className="ar-card">
        <div className="ar-card__body">
          <Empty
            title="Only owners and admins can see this list"
            body="The suppression list holds the contact details of people who asked not to be contacted, so reading it is restricted to the roles that can also act on it. Ask an owner or an admin of your organisation if somebody needs to be added."
          />
        </div>
      </div>
    </div>
  )
}

/* --- the list ------------------------------------------------------------------- */

function SuppressionList(): JSX.Element {
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState<Kind | ''>('')
  const [limit, setLimit] = useState(FIRST_PAGE)
  const [order, setOrder] = useState<Order>(ORDERS.recent)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<Suppression | null>(null)

  // `search`, `kind` and `limit` are dependencies, so changing any of them
  // re-runs the query. Deliberately not debounced, for the reason `InboxPage`
  // states: `useQuery` aborts the previous request on every change, so a fast
  // typist produces one in-flight request rather than a queue of them.
  //
  // `null` rather than `undefined` for an unset filter — the generated types are
  // checked with `exactOptionalPropertyTypes`, so `undefined` is not assignable
  // to `string | null`, and the server reads both as "no filter".
  const list = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/suppression', {
          params: { query: { limit, order, kind: kind || null, search: search || null } },
          signal,
        }),
      ),
    [limit, order, kind, search],
  )

  /**
   * OUTR-91 — the list the tenant is looking at, as a file.
   *
   * **The server renders it**, for the reason `Leads.tsx` gives and one of this
   * screen's own. The table holds fifty of a possible five thousand, so a
   * browser-side export would write the window rather than the list — and the
   * reason to take a copy of a suppression list is to hand the whole of it to a
   * lawyer, a DPO or the next sending platform, which is precisely the case a
   * silent one-page file gets wrong.
   *
   * The filters go with it. Exporting from a screen filtered to *Mobile* and
   * getting every email address back would be the file disagreeing with the
   * table it was taken from.
   *
   * `downloadCsv` still writes it, so the file carries the BOM every export in
   * this app carries — without it Excel on Windows reads UTF-8 as the local code
   * page, and a `.in` domain in a Devanagari brand name arrives as mojibake.
   */
  const download = useMutation(() =>
    call(() =>
      api.GET('/api/v1/outreach/suppression/export', {
        params: { query: { order, kind: kind || null, search: search || null } },
        // The route answers `text/csv`, and openapi-fetch parses as JSON unless
        // told otherwise — which would fail on the first row of a valid file.
        parseAs: 'text',
      }),
    ),
  )

  const onExport = (): void => {
    void (async () => {
      const result = await download.run(undefined)
      // `parseAs: 'text'` types the body `unknown`, because the schema declares
      // `text/csv` with no shape. The guard is what turns that back into a string
      // without asserting one.
      if (result.ok && typeof result.data === 'string') {
        downloadCsv(`augrank-suppression-${new Date().toISOString().slice(0, 10)}.csv`, result.data)
      }
    })()
  }

  /**
   * A press on a header asks the server for that order.
   *
   * It also resets to the first page, because the two are one window: *Contact*
   * over two hundred rows and *Contact* over fifty are different sets, and
   * keeping the enlarged limit would silently hand back a bigger answer to a
   * question the tenant only just changed.
   */
  const sortBy = (value: Order): void => {
    setOrder(value)
    setLimit(FIRST_PAGE)
  }

  const remove = useMutation((suppressionId: string) =>
    call(() =>
      api.DELETE('/api/v1/outreach/suppression/{suppression_id}', {
        params: { path: { suppression_id: suppressionId } },
      }),
    ),
  )

  const filtered = search !== '' || kind !== ''

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Suppression list</h1>
        <span className="app-spacer" />
        <Button
          size="sm"
          onClick={() => {
            setAdding(true)
          }}
        >
          Add manually
        </Button>
      </div>

      <div className="ar-alert ar-alert--info">
        <span className="ar-alert__icon" aria-hidden="true">
          ◷
        </span>
        <div className="ar-alert__body">
          <strong>Nobody on this list is contacted again</strong>
          It is checked before every send, on every channel and in every campaign, and an
          unsubscribe, a bounce or a spam complaint adds somebody to it without asking you. Taking
          an entry off is possible and is recorded in the audit trail with who did it — so do it
          only when the person has asked you to.
        </div>
      </div>

      <div className="ar-card">
        <div className="ar-table-toolbar">
          <input
            className="ar-input"
            type="search"
            placeholder="Search…"
            aria-label="Search the suppression list"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setLimit(FIRST_PAGE)
            }}
          />
          <select
            className="ar-select app-supp__kind"
            aria-label="Kind"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as Kind | '')
              setLimit(FIRST_PAGE)
            }}
          >
            <option value="">Every kind</option>
            {KINDS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="ar-card__spacer" />
          <Count list={list} filtered={filtered} />
          {/* OUTR-128. Last in the row, as drawn: the count says how much there
              is and the button takes it, which is the order the sentence reads
              in. The kind select before the spacer is with the search box
              because both narrow what the count is counting. */}
          <Button
            size="sm"
            tone="ghost"
            busy={download.pending}
            busyLabel="Preparing your file"
            onClick={onExport}
          >
            Export CSV
          </Button>
        </div>

        {/* Under the toolbar rather than inside it: `InlineError` renders a full
            `.ar-alert--danger` block, and as a flex item in `.ar-table-toolbar`
            it would push the search box and the count onto lines of their own —
            the failure `Leads.tsx` hit with the same button. */}
        <InlineError failure={download.state.failure} />

        <ListBody
          list={list}
          filtered={filtered}
          limit={limit}
          order={order}
          onSort={sortBy}
          onShowMore={() => {
            setLimit(MAX_PAGE)
          }}
          onRemove={setRemoving}
          removing={remove.pending}
        />

        <div className="ar-card__footer">
          <span className="ar-subtle ar-text-body-sm">
            An <strong>email</strong> entry stops one address and a <strong>domain</strong> entry
            stops every address at that company. A <strong>mobile</strong> entry stops SMS,
            WhatsApp, RCS and voice alike, because all four reach the same person on the same
            number.{' '}
            {order === ORDERS.contact
              ? 'Sorted by contact, so this is the start of the list alphabetically.'
              : 'Newest first.'}
          </span>
        </div>
      </div>

      {adding ? (
        <AddDialog
          onClose={() => {
            setAdding(false)
          }}
          onAdded={(entry, created) => {
            setAdding(false)
            list.refetch()
            toast.show({
              kind: created ? 'success' : 'info',
              title: created
                ? `${entry.value} will not be contacted`
                : `${entry.value} was already suppressed`,
              body: created
                ? 'Every channel and every campaign, from the next send onwards.'
                : 'Nothing changed — the entry was already on the list.',
            })
          }}
        />
      ) : null}

      {removing ? (
        <RemoveDialog
          entry={removing}
          busy={remove.pending}
          failure={remove.state.failure}
          onClose={() => {
            setRemoving(null)
            remove.reset()
          }}
          onConfirm={() => {
            void (async () => {
              const entry = removing
              const result = await remove.run(entry.id)
              if (!result.ok) return
              setRemoving(null)
              list.refetch()
              toast.show({
                kind: 'success',
                title: `${entry.value} can be contacted again`,
                body: 'The removal is in your audit trail, with your name against it.',
              })
            })()
          }}
        />
      ) : null}
    </div>
  )
}

/* --- the toolbar's count -------------------------------------------------------- */

/**
 * How many, and of what.
 *
 * Two numbers rather than one whenever a filter is on, because the response
 * carries two different facts and only one of them is the search's: `total` is
 * the organisation's whole list and the row count is what matched. See the
 * module docstring.
 */
function Count({
  list,
  filtered,
}: {
  list: ReturnType<typeof useQuery<Schema<'SuppressionListResponse'>>>
  filtered: boolean
}): JSX.Element | null {
  if (list.state.status !== 'ready') return null
  const { suppressions, total } = list.state.data
  const totalLabel = `${String(total)} suppressed`

  return (
    <span className="ar-subtle ar-text-body-sm">
      {filtered ? `${String(suppressions.length)} shown · ${totalLabel} in total` : totalLabel}
    </span>
  )
}

/* --- the table ------------------------------------------------------------------ */

function ListBody({
  list,
  filtered,
  limit,
  order,
  onSort,
  onShowMore,
  onRemove,
  removing,
}: {
  list: ReturnType<typeof useQuery<Schema<'SuppressionListResponse'>>>
  filtered: boolean
  limit: number
  order: Order
  onSort: (order: Order) => void
  onShowMore: () => void
  onRemove: (entry: Suppression) => void
  removing: boolean
}): JSX.Element {
  const brand = useBrand()
  if (list.state.status === 'loading') {
    return (
      <div className="ar-card__body">
        <Loading label="Loading the suppression list" lines={4} />
      </div>
    )
  }

  if (list.state.status === 'error') {
    return (
      <div className="ar-card__body">
        <ErrorState failure={list.state.failure} onRetry={list.refetch} />
      </div>
    )
  }

  const rows = list.state.data.suppressions

  if (rows.length === 0) {
    // The header row stays above the empty state (parity P-SUPP-02): the
    // mockup's `data-table-empty` block sits under the table's own head, so the
    // columns are still named on a list with nothing in it.
    return (
      <>
        <div className="ar-table-wrap" tabIndex={0}>
          <table className="ar-table">
            <Head order={order} onSort={onSort} />
          </table>
        </div>
        <div className="ar-card__body">
          {filtered ? (
            <Empty
              title="No matches"
              body="Nothing on the list matches what you typed. Search matches the stored form of an address, which is always lower case."
            />
          ) : (
            <Empty
              title="Nobody is suppressed"
              body="An unsubscribe link, a hard bounce or a spam complaint adds somebody here automatically. Until one of those happens, this list stays empty — and that is the good state."
            />
          )}
        </div>
      </>
    )
  }

  // The list is `LIMIT n` with no offset, so a full page is the only signal that
  // there may be more. See the module docstring for why the answer is a bigger
  // limit rather than a next page.
  const capped = rows.length >= limit

  return (
    <>
      <div className="ar-table-wrap" tabIndex={0}>
        <table className="ar-table">
          <Head order={order} onSort={onSort} />
          <tbody>
            {rows.map((entry) => (
              <Row key={entry.id} entry={entry} onRemove={onRemove} disabled={removing} />
            ))}
          </tbody>
        </table>
      </div>
      {capped ? (
        <div className="ar-card__body">
          {/*
            **Both sentences name the window the sort actually produced** (OUTR-92).
            They said *most recent* unconditionally, which is the exact failure the
            rest of this screen is built against: under `order=contact` the rows on
            screen are the alphabetically first, and calling them the most recent
            describes a window the list does not have. The cap sentence was worse
            still — *"find one that is older than these"* sends a tenant looking
            down a time axis the list is not sorted on.
          */}
          {limit < MAX_PAGE ? (
            <div className="ar-row">
              <span className="ar-subtle ar-text-body-sm">
                {order === ORDERS.contact
                  ? `Showing the first ${String(limit)} by contact.`
                  : `Showing the ${String(limit)} most recent.`}
              </span>
              <span className="app-spacer" />
              <Button size="sm" onClick={onShowMore}>
                Show more
              </Button>
            </div>
          ) : (
            <span className="ar-subtle ar-text-body-sm">
              {order === ORDERS.contact
                ? `Showing the first ${String(MAX_PAGE)} by contact, which is as many as ${brand.productName} will list at once. Search for an address to find one that sorts after these.`
                : `Showing the ${String(MAX_PAGE)} most recent, which is as many as ${brand.productName} will list at once. Search for an address to find one that is older than these.`}
            </span>
          )}
        </div>
      ) : null}
    </>
  )
}

/** The column heads, shared by the list and its empty state. */
function Head({ order, onSort }: { order: Order; onSort: (order: Order) => void }): JSX.Element {
  return (
    <thead>
      <tr>
        {/* OUTR-92. The two the drawing marks sortable, and only those:
            Reason and Kind are the two columns with a filter of their own
            in the toolbar, and a sort is the worse tool for a four-value
            column — it buries the rows you did not ask about instead of
            removing them. */}
        <SortHeader
          label="Contact"
          direction="ascending"
          active={order === ORDERS.contact}
          onSort={() => {
            onSort(ORDERS.contact)
          }}
        />
        <th>Reason</th>
        <th>Kind</th>
        <SortHeader
          label="Added"
          direction="descending"
          active={order === ORDERS.recent}
          onSort={() => {
            onSort(ORDERS.recent)
          }}
        />
        <th>
          <span className="ar-visually-hidden">Remove</span>
        </th>
      </tr>
    </thead>
  )
}

/**
 * One header that reorders the list on the server.
 *
 * `Leads.tsx`'s component with one difference: the direction is a prop rather
 * than always *descending*, because these two cursors walk opposite ways —
 * newest-first on `Added`, A-to-Z on `Contact` — and `aria-sort` is the one
 * place a screen-reader user learns which. Announcing *descending* on an
 * alphabetical sort would be the control describing itself wrongly, which is the
 * same failure as announcing nothing.
 *
 * **`aria-sort` is on the `<th>`, and repeated on the button.** It is on the
 * cell because that is the only place a screen reader reads it from — on a
 * `role="button"` the attribute is ignored — and on the button because the Atlas
 * layer colours the arrow off `.ar-sort[aria-sort=…]` (`components.css:400`).
 */
function SortHeader({
  label,
  direction,
  active,
  onSort,
}: {
  label: string
  direction: 'ascending' | 'descending'
  active: boolean
  onSort: () => void
}): JSX.Element {
  const state = active ? direction : 'none'
  return (
    <th aria-sort={state}>
      <button type="button" className="ar-sort" aria-sort={state} onClick={onSort}>
        {label}{' '}
        <span className="ar-sort__arrow" aria-hidden="true">
          {active ? (direction === 'ascending' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  )
}

function Row({
  entry,
  onRemove,
  disabled,
}: {
  entry: Suppression
  onRemove: (entry: Suppression) => void
  disabled: boolean
}): JSX.Element {
  const reason = REASONS[entry.reason] ?? { tone: 'neutral' as const, label: entry.reason }

  return (
    <tr>
      <td className="ar-mono app-supp__value">{entry.value}</td>
      <td>
        <span className={`ar-badge ar-badge--${reason.tone}`}>{reason.label}</span>
        {entry.detail ? <span className="app-supp__detail ar-subtle">{entry.detail}</span> : null}
      </td>
      <td>{KIND_LABELS[entry.kind] ?? entry.kind}</td>
      <td className="ar-muted">{formatDateIn(entry.created_at, IST)}</td>
      <td className="ar-num">
        <Button
          tone="quiet"
          size="sm"
          disabled={disabled}
          onClick={() => {
            onRemove(entry)
          }}
        >
          Remove
        </Button>
      </td>
    </tr>
  )
}

/* --- vocabulary ----------------------------------------------------------------- */

/**
 * Mirrors `AddSuppressionRequest.value` in `openapi.json` (`maxLength: 320`) — an
 * address, not prose, which is why it is a fifth of the `maxLength={500}` on the
 * *Note* field below (`AddSuppressionRequest.detail`) (WEB-14).
 */
const VALUE_MAX = 320

/** `SuppressionKind`, in the tenant's words. The four the `CHECK` admits. */
const KIND_LABELS: Record<string, string> = {
  email: 'Email',
  mobile: 'Mobile',
  domain: 'Domain',
  linkedin: 'LinkedIn',
}

const KINDS: { value: Kind; label: string; help: string }[] = [
  { value: 'email', label: 'Email', help: 'One address. priya@example.com' },
  {
    value: 'mobile',
    label: 'Mobile',
    // Outreach on a number is WhatsApp since 2026-09-07; SMS, RCS and voice
    // are no longer sold, and naming them here promised a reach that does not
    // exist (walkthrough 2026-09-12).
    help: 'One number, with its country code. Stops WhatsApp, and anything else on that number.',
  },
  {
    value: 'domain',
    label: 'Domain',
    help: 'Every address at a company. example.com — what a "remove us from your list" reply means.',
  },
  { value: 'linkedin', label: 'LinkedIn', help: 'One profile URL.' },
]

/**
 * `SuppressionReason`, in the tenant's words.
 *
 * `dpdp_request` is deliberately not folded into *Unsubscribed*, for the reason
 * the enum itself gives: one is somebody saying stop emailing me, the other is a
 * Data Principal exercising a statutory right, and only the second comes with a
 * deadline. A screen that showed them as the same badge would lose the
 * distinction at the one moment somebody is looking for it.
 */
const REASONS: Record<string, { tone: 'neutral' | 'warning' | 'danger' | 'info'; label: string }> =
  {
    unsubscribed: { tone: 'neutral', label: 'Unsubscribed' },
    bounced: { tone: 'warning', label: 'Hard bounce' },
    complaint: { tone: 'danger', label: 'Marked as spam' },
    not_interested: { tone: 'danger', label: 'Not interested' },
    manual: { tone: 'neutral', label: 'Added manually' },
    dpdp_request: { tone: 'info', label: 'DPDP request' },
  }

/**
 * The reasons a person can put on an entry by hand.
 *
 * Four of the six. `bounced` and `complaint` are written by the delivery
 * reconciler from what the provider reported (S-G4) — they are observations, not
 * assertions, and a human typing one in would be recording a fact nobody
 * measured. The API still accepts all six, because the machines that write those
 * two go through the same route.
 */
const ADDABLE: { value: Reason; label: string; detail: string }[] = [
  {
    value: 'manual',
    label: 'Added manually',
    detail: 'You are adding them for a reason that is not one of the others.',
  },
  {
    value: 'unsubscribed',
    label: 'They asked to be removed',
    detail: 'They replied, called or told you in person instead of using the link.',
  },
  {
    value: 'not_interested',
    label: 'Not interested',
    detail: 'They said no to the offer, so there is nothing to follow up.',
  },
  {
    value: 'dpdp_request',
    label: 'DPDP erasure request',
    detail: 'They exercised a statutory right. This one carries a deadline — record it as itself.',
  },
]

/* --- adding --------------------------------------------------------------------- */

/**
 * Add somebody by hand — the prototype's *Add manually*.
 *
 * The value is **not** normalised here even though the screen knows how: the
 * route normalises it with the same function the send-time check uses, which is
 * what makes the two agree by construction rather than by review
 * (`app/outreach/suppression.normalise`). A second implementation in TypeScript
 * would be a second opinion about what an address is, and the interesting bugs
 * in a suppression list are exactly the ones where two normalisers disagree.
 */
function AddDialog({
  onClose,
  onAdded,
}: {
  onClose: () => void
  onAdded: (entry: Suppression, created: boolean) => void
}): JSX.Element {
  const [kind, setKind] = useState<Kind>('email')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState<Reason>('manual')
  const [detail, setDetail] = useState('')

  const add = useMutation((body: Schema<'AddSuppressionRequest'>) =>
    call(() => api.POST('/api/v1/outreach/suppression', { body })),
  )

  const chosen = KINDS.find((option) => option.value === kind)

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await add.run({
      kind,
      value: value.trim(),
      reason,
      // `null` rather than an empty string: `detail` is `str | None` on the
      // request and an empty note is not a note.
      detail: detail.trim() || null,
    })
    if (result.ok) onAdded(result.data.suppression, result.data.created)
  }

  return (
    <Modal
      title="Add to the suppression list"
      onClose={onClose}
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button tone="primary" type="submit" form="add-suppression" busy={add.pending}>
            Add
          </Button>
        </>
      }
    >
      <form
        id="add-suppression"
        className="ar-stack"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <InlineError failure={add.state.failure} />
        <Field label="What kind">
          {(props) => (
            <select
              {...props}
              className="ar-select"
              value={kind}
              onChange={(event) => {
                setKind(event.target.value as Kind)
                add.reset()
              }}
            >
              {KINDS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <TextField
          label="Who"
          required
          autoComplete="off"
          maxLength={VALUE_MAX}
          help={chosen?.help}
          value={value}
          onChange={(event) => {
            setValue(event.target.value)
            add.reset()
          }}
        />
        <Field label="Why">
          {() => (
            <div role="radiogroup" aria-label="Why">
              {ADDABLE.map((option) => (
                <label className="ar-choice ar-choice--radio" key={option.value}>
                  <input
                    type="radio"
                    name="suppression-reason"
                    value={option.value}
                    checked={reason === option.value}
                    onChange={() => {
                      setReason(option.value)
                      add.reset()
                    }}
                  />
                  <span className="ar-choice__box" aria-hidden="true" />
                  <span className="ar-choice__text">
                    {option.label}
                    <span className="ar-choice__desc">{option.detail}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </Field>
        <TextField
          label="Note (optional)"
          autoComplete="off"
          maxLength={500}
          help="Kept on the entry and written into the audit trail. Say where the request came from — a reply, a phone call, a meeting."
          value={detail}
          onChange={(event) => {
            setDetail(event.target.value)
            add.reset()
          }}
        />
      </form>
    </Modal>
  )
}

/* --- removing ------------------------------------------------------------------- */

/**
 * Confirm before removing, and say what removing does.
 *
 * Confirmed rather than done on the press, unlike *Remove* on the team panel:
 * the row being deleted is somebody's instruction not to be contacted, the
 * deletion is real (`DELETE … RETURNING`, not a flag), and the next send after
 * it will reach them. There is no undo except adding them back, by which point
 * the message has gone.
 */
function RemoveDialog({
  entry,
  busy,
  failure,
  onClose,
  onConfirm,
}: {
  entry: Suppression
  busy: boolean
  failure: ApiFailure | null
  onClose: () => void
  onConfirm: () => void
}): JSX.Element {
  const brand = useBrand()
  const reason = REASONS[entry.reason] ?? { tone: 'neutral' as const, label: entry.reason }

  return (
    <Modal
      title="Take them off the suppression list?"
      onClose={onClose}
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Keep them suppressed
          </Button>
          <Button tone="danger" busy={busy} onClick={onConfirm}>
            Remove
          </Button>
        </>
      }
    >
      <InlineError failure={failure} />
      <p style={{ marginTop: 0 }}>
        <span className="ar-mono">{entry.value}</span> was added on{' '}
        {formatDateIn(entry.created_at, IST)} — {reason.label.toLowerCase()}.
      </p>
      <p>
        Removing them lets {brand.productName} contact them again on the next send. Do this only if
        they have asked you to; the removal goes into your audit trail with your name and the entry
        that was deleted.
      </p>
    </Modal>
  )
}
